import { useState } from "react";
import { MdAdd, MdEdit } from "react-icons/md";
import { PaziaLoader, PaziaQuill } from "components/atoms";
import { PaziaModal } from "components/molecules";
import { useCourseGroupShopSettings, CourseGroupShopSettingsApi } from "api/coursegroups";
import { coursegroupsApi } from "api/coursegroups";
import { useToaster } from "utils";
import { routes } from "consts/routes";
import { TrainerBlacklistModal } from "./TrainerBlacklistModal";

export const ShopCourseGroupSettingsForm = () => {
  const { isLoading, settings, invalidate } = useCourseGroupShopSettings();
  const { showSuccess, showError } = useToaster();
  const [blacklistModal, setBlacklistModal] = useState<{ id: number; name: string } | null>(null);
  const [textModal, setTextModal] = useState<CourseGroupShopSettingsApi | null>(null);
  const [productCardTextModal, setProductCardTextModal] = useState<CourseGroupShopSettingsApi | null>(null);
  const [search, setSearch] = useState("");
  const [textEdits, setTextEdits] = useState<Record<number, string>>({});
  const [productCardTextEdits, setProductCardTextEdits] = useState<Record<number, string>>({});

  const handleToggle = async (
    group: CourseGroupShopSettingsApi,
    field: "hideScheduleTab" | "hidePrice" | "showOngoingCourses",
  ) => {
    const updated = {
      hideScheduleTab: field === "hideScheduleTab" ? !group.hideScheduleTab : group.hideScheduleTab,
      hidePrice: field === "hidePrice" ? !group.hidePrice : group.hidePrice,
      scheduleTabCustomText: group.scheduleTabCustomText,
      productCardCustomText: group.productCardCustomText,
      showOngoingCourses: field === "showOngoingCourses" ? !group.showOngoingCourses : group.showOngoingCourses,
    };
    try {
      await coursegroupsApi.updateShopSettings(group.courseGroupId, updated);
      showSuccess("Zapisano");
      invalidate();
    } catch {
      showError("Nie udało się zapisać");
    }
  };

  const handleSaveText = async () => {
    if (!textModal) return;
    const text = textEdits[textModal.courseGroupId] ?? textModal.scheduleTabCustomText ?? "";
    try {
      await coursegroupsApi.updateShopSettings(textModal.courseGroupId, {
        hideScheduleTab: textModal.hideScheduleTab,
        hidePrice: textModal.hidePrice,
        scheduleTabCustomText: text,
        productCardCustomText: textModal.productCardCustomText,
        showOngoingCourses: textModal.showOngoingCourses,
      });
      showSuccess("Zapisano");
      invalidate();
      setTextModal(null);
    } catch {
      showError("Nie udało się zapisać");
    }
  };

  const handleSaveProductCardText = async () => {
    if (!productCardTextModal) return;
    const text =
      productCardTextEdits[productCardTextModal.courseGroupId] ??
      productCardTextModal.productCardCustomText ??
      "";
    try {
      await coursegroupsApi.updateShopSettings(productCardTextModal.courseGroupId, {
        hideScheduleTab: productCardTextModal.hideScheduleTab,
        hidePrice: productCardTextModal.hidePrice,
        scheduleTabCustomText: productCardTextModal.scheduleTabCustomText,
        productCardCustomText: text,
        showOngoingCourses: productCardTextModal.showOngoingCourses,
      });
      showSuccess("Zapisano");
      invalidate();
      setProductCardTextModal(null);
    } catch {
      showError("Nie udało się zapisać");
    }
  };

  const getTextValue = (group: CourseGroupShopSettingsApi) =>
    textEdits[group.courseGroupId] !== undefined
      ? textEdits[group.courseGroupId]
      : (group.scheduleTabCustomText ?? "");

  const getProductCardTextValue = (group: CourseGroupShopSettingsApi) =>
    productCardTextEdits[group.courseGroupId] !== undefined
      ? productCardTextEdits[group.courseGroupId]
      : (group.productCardCustomText ?? "");

  const filtered = settings.filter((g) =>
    g.name.toLowerCase().includes(search.toLowerCase()),
  );

  if (isLoading) return <PaziaLoader position="center" />;

  return (
    <div>
      <h3 className="text-lg font-medium text-slate-800 dark:text-slate-200 mb-1">Ustawienia sklepu per grupa kursów</h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
        Ukrycie zakładki Terminy lub ceny wpływa na stronę produktu w sklepie. Trwające edycje kursów są domyślnie
        ukryte w sklepie — włącz „Pokazuj trwające edycje”, aby były widoczne (zapis jest możliwy tylko wtedy, gdy
        kurs ma włączone zapisy po rozpoczęciu).
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-700">
              <th className="text-left py-2 pr-6 font-medium text-slate-600 dark:text-slate-400">
                <input
                  type="text"
                  placeholder="Szukaj grupy kursów..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full px-2 py-1 text-sm font-normal rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:border-primary-400"
                />
              </th>
              <th className="text-center py-2 px-4 font-medium text-slate-600 dark:text-slate-400">Zastąp zakładkę Terminy</th>
              <th className="text-center py-2 px-4 font-medium text-slate-600 dark:text-slate-400">Ukryj cenę (lista oczekujących)</th>
              <th className="text-center py-2 px-4 font-medium text-slate-600 dark:text-slate-400">Pokazuj trwające edycje</th>
              <th className="text-center py-2 px-4 font-medium text-slate-600 dark:text-slate-400">Tekst na kafelku produktu</th>
              <th className="text-center py-2 px-4 font-medium text-slate-600 dark:text-slate-400">Wykładowcy</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((group) => (
              <tr key={group.courseGroupId} className="border-b border-slate-100 dark:border-slate-800">
                <td className="py-3 pr-6">
                  <a
                    href={routes.COURSE_GROUPS_EDIT(String(group.courseGroupId))}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 hover:underline"
                  >
                    {group.name}
                  </a>
                </td>
                <td className="py-3 px-4 text-center">
                  <div className="flex flex-col items-center gap-1">
                    <ToggleSwitch
                      checked={group.hideScheduleTab}
                      onChange={() => handleToggle(group, "hideScheduleTab")}
                    />
                    {group.hideScheduleTab && (
                      <TextPreviewButton
                        value={group.scheduleTabCustomText}
                        onClick={() => setTextModal(group)}
                      />
                    )}
                  </div>
                </td>
                <td className="py-3 px-4 text-center">
                  <ToggleSwitch
                    checked={group.hidePrice}
                    onChange={() => handleToggle(group, "hidePrice")}
                  />
                </td>
                <td className="py-3 px-4 text-center">
                  <ToggleSwitch
                    checked={group.showOngoingCourses}
                    onChange={() => handleToggle(group, "showOngoingCourses")}
                  />
                </td>
                <td className="py-3 px-4 text-center">
                  <TextPreviewButton
                    value={group.productCardCustomText}
                    onClick={() => setProductCardTextModal(group)}
                  />
                </td>
                <td className="py-3 px-4 text-center">
                  <button
                    type="button"
                    onClick={() => setBlacklistModal({ id: group.courseGroupId, name: group.name })}
                    className="text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 underline"
                  >
                    Zarządzaj
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="py-6 text-center text-slate-400">
                  {search ? "Brak wyników dla podanej frazy" : "Brak grup kursów"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <TrainerBlacklistModal
        courseGroupId={blacklistModal?.id ?? 0}
        courseGroupName={blacklistModal?.name ?? ""}
        isOpen={blacklistModal !== null}
        onClose={() => setBlacklistModal(null)}
      />

      <PaziaModal
        id="schedule-tab-text-modal"
        isOpen={textModal !== null}
        onCloseRequest={() => setTextModal(null)}
        size="lg"
      >
        <div className="p-4">
          <h3 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-1">
            Tekst zakładki Terminy
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
            {textModal?.name}
          </p>
          {textModal && (
            <PaziaQuill
              value={getTextValue(textModal)}
              onChange={(val) => setTextEdits((prev) => ({ ...prev, [textModal.courseGroupId]: val }))}
            />
          )}
          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={() => setTextModal(null)}
              className="px-4 py-1.5 text-sm text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              Anuluj
            </button>
            <button
              type="button"
              onClick={handleSaveText}
              className="px-4 py-1.5 text-sm bg-primary-500 text-white rounded hover:bg-primary-600 transition-colors"
            >
              Zapisz
            </button>
          </div>
        </div>
      </PaziaModal>

      <PaziaModal
        id="product-card-text-modal"
        isOpen={productCardTextModal !== null}
        onCloseRequest={() => setProductCardTextModal(null)}
        size="lg"
      >
        <div className="p-4">
          <h3 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-1">
            Tekst na kafelku produktu
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
            {productCardTextModal?.name}
          </p>
          {productCardTextModal && (
            <textarea
              value={getProductCardTextValue(productCardTextModal)}
              onChange={(e) =>
                setProductCardTextEdits((prev) => ({
                  ...prev,
                  [productCardTextModal.courseGroupId]: e.target.value,
                }))
              }
              rows={4}
              className="w-full px-3 py-2 text-sm rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:border-primary-400"
            />
          )}
          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={() => setProductCardTextModal(null)}
              className="px-4 py-1.5 text-sm text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              Anuluj
            </button>
            <button
              type="button"
              onClick={handleSaveProductCardText}
              className="px-4 py-1.5 text-sm bg-primary-500 text-white rounded hover:bg-primary-600 transition-colors"
            >
              Zapisz
            </button>
          </div>
        </div>
      </PaziaModal>
    </div>
  );
};

function stripHtml(value: string) {
  return value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function TextPreviewButton({
  value,
  onClick,
}: {
  value?: string;
  onClick: () => void;
}) {
  const preview = value ? stripHtml(value) : "";

  if (!preview) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1 rounded-md border border-dashed border-slate-300 dark:border-slate-600 px-2 py-1 text-xs text-slate-400 dark:text-slate-500 hover:border-primary-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
      >
        <MdAdd size={14} />
        Dodaj tekst
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      title={preview}
      className="group inline-flex max-w-[200px] items-center gap-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 px-2 py-1 text-left hover:border-primary-400 hover:bg-primary-50/60 dark:hover:bg-slate-700 transition-colors"
    >
      <span className="truncate text-xs text-slate-700 dark:text-slate-300">{preview}</span>
      <MdEdit
        size={13}
        className="shrink-0 text-slate-400 group-hover:text-primary-500 dark:group-hover:text-primary-400"
      />
    </button>
  );
}

function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? "bg-primary-500" : "bg-slate-300 dark:bg-slate-600"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}
